/**
 * ProfileDropdown - Header dropdown for user profile and logout
 * Replaces the simple logout button with a profile menu
 */
import React from 'react';
import { User, Settings, LogOut, ChevronDown, Moon, Sun } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ProfileAvatar } from './ProfileAvatar';

export const ProfileDropdown = ({ 
  user, 
  onLogout, 
  onOpenProfile,
  onOpenSettings,
  isDarkMode,
  onToggleTheme
}) => {
  const displayName = user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'User';
  const role = user?.role || '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex items-center gap-2 px-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          data-testid="profile-dropdown-trigger"
        >
          <ProfileAvatar user={user} size="sm" />
          <div className="hidden sm:flex flex-col items-start">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 max-w-[120px] truncate">
              {displayName}
            </span>
            {role && (
              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{role}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400 hidden sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 dark:bg-[#242424] dark:border-[#404040]">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none dark:text-gray-100">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="dark:bg-[#404040]" />
        <DropdownMenuItem 
          onClick={onOpenProfile}
          className="cursor-pointer dark:text-gray-200 dark:focus:bg-[#2D2D2D]"
          data-testid="profile-menu-item"
        >
          <User className="mr-2 h-4 w-4" />
          <span>My Profile</span>
        </DropdownMenuItem>
        {onOpenSettings && (
          <DropdownMenuItem 
            onClick={onOpenSettings}
            className="cursor-pointer dark:text-gray-200 dark:focus:bg-[#2D2D2D]"
            data-testid="settings-menu-item"
          >
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="dark:bg-[#404040]" />
        <DropdownMenuItem 
          onClick={onToggleTheme}
          className="cursor-pointer dark:text-gray-200 dark:focus:bg-[#2D2D2D]"
          data-testid="theme-toggle-menu-item"
        >
          {isDarkMode ? (
            <>
              <Sun className="mr-2 h-4 w-4" />
              <span>Light Mode</span>
            </>
          ) : (
            <>
              <Moon className="mr-2 h-4 w-4" />
              <span>Dark Mode</span>
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="dark:bg-[#404040]" />
        <DropdownMenuItem 
          onClick={onLogout}
          className="cursor-pointer text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
          data-testid="logout-menu-item"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileDropdown;
